import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { Distributor, DistributorOrganization } from "@models/Distributor";
import client, { RequestParams, useMutateError } from "@services/api";
import { buildPaginatedQuery } from "@utils/reactQuery";

export const distributorsKeyFactory = createQueryKeys("distributors", {
  detail: (id: string) => ({
    queryKey: [id],
    queryFn: () =>
      client.get<Distributor>(`distributors/${id}`).then((res) => res.data),
  }),
  paginated: (params: RequestParams) => ({
    queryKey: [params],
    queryFn: () => client.get<Distributor[]>("distributors", { params }),
  }),
});

export const useDistributorQuery = (
  id: string | null | undefined,
  params?: RequestParams
) => {
  return useQuery({
    ...distributorsKeyFactory.detail(id!),
    enabled: !!id,
    ...params,
  });
};

export const usePaginatedDistributorsQuery = buildPaginatedQuery(
  distributorsKeyFactory.paginated,
  {
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
  }
);

type CreateDistributorPayload = {
  name: string;
};

export const useCreateDistributorMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ name }: CreateDistributorPayload) =>
      await client
        .post<Distributor>("distributors", {
          name,
        })
        .then((res) => res.data),
    onSuccess: (distributor) => {
      queryClient.invalidateQueries({
        queryKey: distributorsKeyFactory.paginated._def,
      });
      queryClient.setQueryData<Distributor>(
        distributorsKeyFactory.detail(distributor.id).queryKey,
        distributor
      );
    },
  });
};

type UpdateDistributorPayload = {
  id: string;
  name?: string;
  ordererIds: string[];
};

export const useUpdateDistributorMutation = () => {
  const queryClient = useQueryClient();
  const setError = useMutateError();
  return useMutation({
    mutationFn: ({ id, ordererIds, ...attrs }: UpdateDistributorPayload) =>
      client
        .put<Distributor>(`distributors/${id}`, {
          ...attrs,
          orderersIds: ordererIds,
        })
        .then((res) => res.data),
    onSuccess: (distributor) => {
      queryClient.invalidateQueries({
        queryKey: distributorsKeyFactory.paginated._def,
      });
      queryClient.setQueryData<Distributor>(
        distributorsKeyFactory.detail(distributor.id).queryKey,
        distributor
      );
    },
    onError: (err) => setError(err, "Update Distributor"),
  });
};

export type CreateDistributorOrganizationPayload = {
  distributorId: string;
  organizationId: string;
  brandIds?: string[];
};

export const useCreateDistributorOrganizationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      brandIds,
      distributorId,
      organizationId,
    }: CreateDistributorOrganizationPayload) =>
      client.post(`distributor-organizations`, {
        allowBudgetCheckout: false,
        allowStripeCheckout: false,
        allowStandardCheckout: false,
        brandsIds: brandIds,
        distributorId: +distributorId,
        organizationId: +organizationId,
      }),
    onSuccess: async (_, { distributorId }) => {
      await queryClient.invalidateQueries({
        queryKey: distributorsKeyFactory.detail(distributorId).queryKey,
      });
    },
  });
};

export type UpdateDistributorOrganizationPayload = {
  id: string;
  distributorId: string;
  organizationId: string;
  allowBudgetCheckout?: boolean;
  allowStripeCheckout?: boolean;
  allowStandardCheckout?: boolean;
  brandIds: string[];
  territoryIds: string[];
  channelIds: string[];
};

export const useUpdateDistributorOrganizationMutation = () => {
  const queryClient = useQueryClient();
  const setError = useMutateError();
  return useMutation({
    mutationFn: ({
      id,
      brandIds,
      territoryIds,
      channelIds,
      ...data
    }: UpdateDistributorOrganizationPayload) =>
      client.put<DistributorOrganization>(`distributor-organizations/${id}`, {
        ...data,
        distributorId: +data.distributorId,
        organizationId: +data.organizationId,
        brandsIds: brandIds,
        channelsIds: channelIds,
        territoriesIds: territoryIds,
      }),
    onSuccess: async (_, { distributorId }) => {
      await queryClient.invalidateQueries({
        queryKey: distributorsKeyFactory.detail(distributorId).queryKey,
      });
    },
    onError: (err) => setError(err, "Update Distributor Organization"),
  });
};

export type DeleteDistributorOrganizationPayload = {
  distributorId: string;
  id: string;
};

export const useDeleteDistributorOrganizationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: DeleteDistributorOrganizationPayload) =>
      client.delete(`distributor-organizations/${id}`),
    onSuccess: (deletedDistOrg, { distributorId }) => {
      queryClient.setQueryData<Distributor>(
        distributorsKeyFactory.detail(distributorId).queryKey,
        (dist) =>
          dist && {
            ...dist,
            distributorOrganizations: dist.distributorOrganizations.filter(
              (distOrg) => distOrg.id !== deletedDistOrg.id
            ),
          }
      );
    },
  });
};
